<template>
	<ajax-form :action="props.edit ? props.modelConfig.update_ep_resolver(model, parentModel) : props.modelConfig.store_ep_resolver(parentModel)"
			   :data="props.data"
			   ref="formElement"
			   :notification="!redirectPath || props.edit"
			   :without-feedback="!props.edit && !!redirectPath"
			   :confirm="props.confirm"
			   v-model:loading="isLoading"
			   v-model:valid="isValid"
			   :method="props.edit ? (props.modelConfig.update_method ?? 'PUT') : (props.modelConfig.store_method ?? 'POST')" @success="handleSuccess">

		<slot/>

		<wrapper v-if="!props.hideButtons || $slots.buttons" class="mt-16 mb-20 px-16 sm:px-0" break-column="xs" grow>
			<template v-if="!$slots.buttons && !props.hideButtons">
				<form-btn v-if="destroyable" :action="props.modelConfig.destroy_ep_resolver(props.model, props.parentModel)"
						  :method="props.modelConfig.destroy_method_resolver(props.model, props.parentModel)"
						  color="error"
						  @success="handleDeleteSuccess"
						  outlined
						  notification="destroyed"
						  v-model:loading="isDeleting"
						  :disabled="props.disabled || isLoading || isDeleting"
						  :confirm="deleteConfirm">
					<Icon :icon="icons.close"/>
					<span v-if="props.modelConfig.destroy_label_resolver">{{ props.modelConfig.destroy_label_resolver(props.model, props.parentModel) }}</span>
					<span v-else>{{ $t('action.destroy') }}</span>
				</form-btn>

				<slot name="button"/>

				<btn :loading="isLoading" :disabled="props.disabled || isLoading || isDeleting" color="success" type="submit">
					<Icon :icon="icons.save"/>
					<span v-if="!props.edit">{{ props.modelConfig.store_label_resolver ? props.modelConfig.store_label_resolver(props.parentModel) : $t('action.create') }}</span>
					<span v-else>{{ props.modelConfig.update_label_resolver ? props.modelConfig.update_label_resolver(props.model, props.parentModel) : $t('action.save') }}</span>
				</btn>
			</template>
			<slot name="buttons"/>
		</wrapper>
	</ajax-form>
</template>

<script setup lang="ts">
import {useRouter, useRoute, useNuxtApp, useI18n} from "#imports";
import {closeIcon, saveIcon} from "../../config";
import type {Model} from "@spoferan/spoferan-ts-core";
import {ref, watch, computed} from "vue";

const $router = useRouter();
const $route = useRoute();
const {t} = useI18n();
const {$confirm} = useNuxtApp();

const props = withDefaults(defineProps<{
	modelConfig: { [key: string]: any },

	// The data to send to create or update the model
	data: {},

	// The model to display the details for
	model?: Model | null, // Might be null when fetch loaded

	// The parent model of the model the displayed page belongs to
	parentModel?: Model,

	// The confirmation message to show before submitting the form
	confirm?: {},

	// Whether updates are disabled
	disabled?: boolean,

	// Whether the form is used to update the model
	edit?: boolean,

	// The valid prop to keep in sync with the form's valid prop
	valid?: boolean,

	// The loading prop to keep in sync with the form's loading prop
	loading?: boolean,

	// Whether the button area including the submit button shall be hidden
	hideButtons?: boolean,

	// A function that receives the response data and response object to resolve an additional success message to display when the request was successful.
	// Example: (data, response) => t('success')
	successMessageResolver?: (data: {}, response: {}) => string,

	// Whether to prevent displaying the success message or applying a redirect for edits after a successful request
	preventSuccessAction?: boolean,

}>(), {});

const emit = defineEmits(['success', 'reset', 'update:valid', 'update:loading']);

const isValid = ref(props.valid);
watch(isValid, (newValue) => {
	emit('update:valid', newValue)
});

const isLoading = ref(props.loading);
watch(isLoading, (newValue) => {
	emit('update:loading', newValue)
});

const isDeleting = ref(false);
const formElement = ref(null);

const icons = {
	close: closeIcon,
	save: saveIcon,
};

const destroyable = computed(() => {
	return props.edit && props.model && props.modelConfig.permissions.delete(props.model, props.parentModel) && !!props.modelConfig.destroy_ep_resolver;
});

// The data of the confirmation dialog to display before deleting the model
const deleteConfirm = computed(() => {
	const confirmData = {
		title: t(`confirm.${props.modelConfig.config_key}.destroy.title`),
		message: t(`confirm.${props.modelConfig.config_key}.destroy.message`, props.modelConfig.lang_params_resolver(props.model, props.parentModel)),
		type: 'error'
	};

	// Check if a specific message shall be displayed
	if (props.modelConfig.destroy_confirm_resolver) {
		return {
			...confirmData,
			...props.modelConfig.destroy_confirm_resolver(props.model, props.parentModel)
		}
	}

	return confirmData;
});

const redirectPath = computed(() => {
	return $route.query.redirect ? $route.query.redirect : (props.modelConfig.index_route_resolver ? props.modelConfig.index_route_resolver(props.parentModel) : null);
});

function handleSuccess(data, response) {
	emit('success', data, response);

	if (props.preventSuccessAction) {
		return;
	}

	// If we have a redirect path for the created / updated model, we ask if the user wants to stay on the page or wants to be redirected
	if (!redirectPath.value) {
		return;
	}

	// If we are on the edit page, we automatically redirect the user to the redirect path after saving
	// This might be extended by an additional prop to ask the user if he wants to be redirected (as on the legacy logic), when the user feedback requires so
	if (props.edit) {
		$router.replace(redirectPath.value);
		return;
	}

	let title = t('confirm.add_another.title');
	let message = t('confirm.add_another.message');
	if (props.successMessageResolver) {
		message = props.successMessageResolver(data, response) + `<br/>${message}`;
	} else if (response.message) {
		message = `${response.message}<br/>${message}`;
	} else if (response.alert && response.alert.message) {
		title = response.alert.title ? response.alert.title : title;
		message = `${response.alert.message}<br/>${message}`;
	} else if (response.notification && response.notification.message) {
		title = response.notification.title ? response.notification.title : title;
		message = `${response.notification.message}<br/>${message}`;
	}

	$confirm({
		title: title,
		text: message,
		type: 'success',
		confirmText: t(`confirm.add_another.accept`),
		cancelText: t(`confirm.add_another.cancel`),
	}).then(({confirmed}) => {
		if (confirmed) {
			if (!props.edit) {
				emit('reset');
			}
			setTimeout(() => {
				if (formElement.value) {
					formElement.value.clearValidation();
				}
			}, 0);
		} else {
			$router.replace(redirectPath.value)
		}
	})
}

function handleDeleteSuccess() {
	// Ensure deletion on edit page that origins from detail page are not redirected back after deletion
	if (!props.modelConfig.show_route_resolver || redirectPath.value !== props.modelConfig.show_route_resolver(props.model, props.parentModel)) {
		$router.replace(redirectPath.value)
	} else {
		$router.replace(props.modelConfig.index_route_resolver(props.parentModel))
	}
}

async function submit() {
	if (formElement.value) {
		return formElement.value.validationSubmit();
	}
}

defineExpose({
	submit,
});
</script>